import React, { ReactNode, useState } from 'react';
import 'components/stepper-form/stepper-form.scss';
import { Button, Form, FormProps, Layout, Row, Space, Steps, Typography } from 'antd';
import { FormInstance } from 'antd/lib/form';
import { NamePath } from 'antd/lib/form/interface';

export interface StepValidationField {
    name: NamePath;
    label: string;
}

export interface StepperFormProps<Values = any> extends FormProps {
    form: FormInstance<Values>;
    steps: {
        key: number;
        validationFields: StepValidationField[] | ((values: Values) => StepValidationField[]);
        title?: string;
        description?: string;
        component: ReactNode;
        isValid: boolean;
    }[];
    previousButtonLabel: string;
    nextButtonLabel: string;
    lastButtonLabel: string;
    isLoading: boolean;
}

const StepperForm: React.FC<StepperFormProps> = (props) => {
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [missingFieldLabels, setMissingFieldLabels] = useState<string[]>([]);

    const currentStep = props.steps[currentStepIndex];
    const nextButtonText = currentStepIndex < props.steps.length - 1 ? props.nextButtonLabel : props.lastButtonLabel;

    const moveForward = async () => {
        const validationFields =
            typeof currentStep.validationFields === 'function'
                ? currentStep.validationFields(props.form.getFieldsValue(true))
                : currentStep.validationFields;

        try {
            await props.form.validateFields(validationFields.map((field) => field.name));
            setMissingFieldLabels([]);

            const nextStepIndex = currentStepIndex + 1;
            if (nextStepIndex < props.steps.length) {
                setCurrentStepIndex(nextStepIndex);
            } else if (nextStepIndex === props.steps.length) {
                props.form.submit();
            }
        } catch (errorInfo) {
            const errorFields: { name: (string | number)[]; errors: string[] }[] = (errorInfo as any).errorFields ?? [];
            const validationPaths = validationFields.map((field) => ({
                ...field,
                path: Array.isArray(field.name) ? field.name : [field.name],
            }));
            const labels = errorFields
                .map((field) => {
                    const matchingField = validationPaths
                        .filter(
                            (validationField) =>
                                validationField.path.length <= field.name.length &&
                                validationField.path.every((segment, index) => field.name[index] === segment),
                        )
                        .sort((left, right) => right.path.length - left.path.length)[0];
                    return matchingField?.label;
                })
                .filter((label): label is string => !!label);
            setMissingFieldLabels(Array.from(new Set(labels)));
        }
    };

    const moveBackwards = () => {
        const nextStepIndex = currentStepIndex - 1;
        if (nextStepIndex >= 0) {
            setMissingFieldLabels([]);
            setCurrentStepIndex(nextStepIndex);
        }
    };

    const StepperButtons = (
        <Row justify={'end'} className={'step-navigator'}>
            <Space>
                {currentStepIndex !== 0 ? (
                    <Button onClick={moveBackwards} disabled={props.isLoading}>
                        {props.previousButtonLabel}
                    </Button>
                ) : null}

                <Button
                    disabled={!currentStep.isValid}
                    type={'primary'}
                    onClick={moveForward}
                    loading={props.isLoading}
                >
                    <Typography.Text style={{ color: 'white' }}>{nextButtonText}</Typography.Text>
                </Button>
            </Space>
        </Row>
    );

    return (
        <Layout className={'stepper-form'}>
            <Steps current={currentStepIndex}>
                {props.steps.map((step) => (
                    <Steps.Step key={step.title} title={step.title} description={step.description} />
                ))}
            </Steps>

            <Form colon={false} {...props}>
                {props.steps.map((step) => {
                    return (
                        <div className={currentStep.key == step.key ? 'current-step' : 'hidden-step'} key={step.key}>
                            {step.component}
                        </div>
                    );
                })}
            </Form>
            {missingFieldLabels.length > 0 ? (
                <div role="alert" className="step-validation-summary">
                    {missingFieldLabels.map((label) => (
                        <Typography.Paragraph key={label} style={{ color: 'red', marginBottom: 0 }}>
                            Falta completar: {label}
                        </Typography.Paragraph>
                    ))}
                </div>
            ) : null}
            {StepperButtons}
        </Layout>
    );
};

export default StepperForm;
